import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { MessageCircle, BookOpen, Lightbulb, Brain, Zap, FileText, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const SolutionsPage = () => {
  return (
    <>
      <Helmet>
        <title>Learning Solutions - PhysicsNotebook.in | Choose Your Learning Path</title>
        <meta name="description" content="Explore our learning solutions: personalized physics mentorship, comprehensive notes bundle, and test series for boards, JEE, and NEET preparation. Structured learning paths for success." />
      </Helmet>

      {/* Header Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 section-padding">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            Learning Solutions
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Choose how you want to master Physics — personal mentorship, detailed notes, and board exam test series.
          </motion.p>
        </div>
      </section>

      {/* Three Pricing Plans - positioned just above "Why Choose" section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">

            {/* Mentorship Plan - Featured */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="relative bg-white rounded-2xl shadow-xl border-2 border-blue-300 p-8 card-hover lg:scale-105 ring-2 ring-blue-100 ring-offset-2"
            >

              <div className="text-center mb-8">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Brain className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Personalized Physics Mentorship</h3>

                <div className="mb-6">
                  <div className="flex items-center justify-center mb-2">
                    <span className="text-3xl font-bold text-gray-500 line-through mr-3">₹6,500</span>
                    <span className="text-4xl font-bold text-green-600">₹4,500</span>
                  </div>
                  <p className="text-sm text-gray-500">Limited time offer</p>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="bg-blue-100 p-1 rounded-full mr-3 mt-1">
                    <MessageCircle className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Personalized Mentorship Sessions</p>
                    <p className="text-sm text-gray-600">One-hour one-on-one calls for deep guidance and strategy.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-blue-100 p-1 rounded-full mr-3 mt-1">
                    <BookOpen className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Custom Study Plan</p>
                    <p className="text-sm text-gray-600">Assess your strengths, fix weak areas, and follow a tailored roadmap.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-blue-100 p-1 rounded-full mr-3 mt-1">
                    <Zap className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Exam Strategy & Time Management </p>
                    <p className="text-sm text-gray-600">Learn how to approach questions smartly and manage pressure effectively.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-blue-100 p-1 rounded-full mr-3 mt-1">
                    <FileText className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Full Revision Notes</p>
                    <p className="text-sm text-gray-600">Complete, structured notes covering your entire syllabus.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-blue-100 p-1 rounded-full mr-3 mt-1">
                    <Award className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Practice Tests & Feedback</p>
                    <p className="text-sm text-gray-600">Mentor-guided assessments with detailed performance feedback.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <Button asChild className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg font-semibold">
                  <Link to="/mentorship-info">Learn More →</Link>
                </Button>
              </div>
            </motion.div>

            {/* Notes Bundle */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="relative bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8 card-hover"
            >
              <div className="text-center mb-8">
                <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="h-8 w-8 text-gray-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Complete Notes Bundle</h3>
                <p className="text-gray-600 mb-4 text-sm">Self-Study Option</p>

                <div className="mb-6">
  <p className="text-base text-green-600 font-semibold">Coming Soon</p>
</div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="bg-gray-100 p-1 rounded-full mr-3 mt-1">
                    <BookOpen className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">5 Comprehensive Modules</p>
                    <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1">
                      <li>Mechanics</li>
                      <li>Thermodynamics</li>
                      <li>Oscillations/Waves/Optics</li>
                      <li>Electricity & Magnetism</li>
                      <li>Modern Physics</li>
                    </ul>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-gray-100 p-1 rounded-full mr-3 mt-1">
                    <Lightbulb className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Revision-Friendly Format</p>
                    <p className="text-sm text-gray-600">Cleanly formatted for quick review and retention</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-gray-100 p-1 rounded-full mr-3 mt-1">
                    <Zap className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Key Formulas & Concepts</p>
                    <div className="text-sm text-gray-600">
                      <p>Essential concepts with question patterns for:</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">BOARD</span>
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">JEE</span>
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs font-medium rounded">NEET</span>
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">CET</span>
                        <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs font-medium rounded">SAT</span>
                        <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 text-xs font-medium rounded">OLYMPIADS</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-gray-100 p-1 rounded-full mr-3 mt-1">
                    <Award className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Solved Examples & Practice</p>
                    <p className="text-sm text-gray-600">Step-by-step solutions and practice problems with answers</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Button asChild className="w-full bg-gray-900 hover:bg-gray-800 text-white py-3 text-lg font-semibold">
                  <Link to="/dpp-notes-info">Learn More →</Link>
                </Button>
              </div>
            </motion.div>

            {/* Test Series */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8 card-hover"
            >
              <div className="text-center mb-8">
                <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="h-8 w-8 text-gray-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Board Exam Test Series</h3>
                <p className="text-gray-600 mb-4 text-sm">Practice Real Papers. Get Real Feedback.</p>

                <div className="mb-6">
                  <p className="text-base text-gray-500">Starts from <span className="font-bold text-lg text-green-600">₹1,499</span></p>
                </div>
              </div>

              {/* Full Series */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                <h4 className="font-semibold text-blue-600 mb-2">Full Series — ₹2,499</h4>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li>• Comprehensive 10-test series covering full syllabus</li>
                  <li className="font-medium text-blue-700 flex items-start">
                    <span className="inline-flex items-center justify-center w-5 h-5 mr-2 text-blue-500">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                        <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <span>Includes all <span className="font-semibold">Mini Series</span> benefits</span>
                  </li>
                  <li>• Two personalized 45-minute exam preparation sessions</li>
                  <li>• Expert guidance on exam writing techniques with model answers</li>
                </ul>
              </div>

              {/* Mini Series */}
              <div className="mb-6 p-4 bg-green-50 rounded-lg border-2 border-green-200">
                <h4 className="font-semibold text-green-700 mb-2">Mini Series — ₹1,499</h4>
                <ul className="text-sm text-gray-600 space-y-1 mb-4">
                  <li>• 5 full syllabus descriptive tests</li>
                  <li>• Manual evaluation with detailed comments</li>
                  <li>• Performance & progress tracking</li>
                  <li>• Feedback via written report</li>
                </ul>
              </div>

              <div className="space-y-3">
                <Button asChild className="w-full bg-gray-900 hover:bg-gray-800 text-white py-3 text-lg font-semibold">
                  <Link to="/test-series-info">Learn More →</Link>
                </Button>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Why Students Choose Physics Notebook */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Students Choose Physics Notebook</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Trusted by learners across boards and entrance exams for structured, effective preparation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Trusted Expertise</h3>
              <p className="text-gray-600">Real educators with proven track records helping students excel in boards and entrance exams</p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Structured Content</h3>
              <p className="text-gray-600">Clear, organized notes and mentorship focused on improvement, not information overload</p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Brain className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Real Progress</h3>
              <p className="text-gray-600">Every plan designed for measurable improvement with personalized feedback and guidance</p>
            </div>
          </div>
        </div>
      </section>

      {/* Consultation CTA */}
      <section className="section-padding bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Still Unsure Which Plan to Choose?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Message us directly for a quick consultation — we'll guide you based on your board, syllabus, and goals.
            </p>
            <Button 
              asChild
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold flex items-center mx-auto"
            >
              <a 
                href="https://wa.me/919309242445?text=Hi%20there!%20I'd%20like%20to%20learn%20more%20about%20your%20mentorship%20programs." 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 no-underline"
              >
                <MessageCircle className="h-5 w-5" />
                Chat with us →
              </a>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Optional Footer CTA */}
      <section className="section-padding bg-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg text-gray-600">
            Need group plans for schools or coaching batches? Email us at{' '}
            <a 
              href="mailto:aditya@physicsnotebook.in?subject=Group%20Pricing%20Inquiry"
              className="text-blue-600 hover:underline"
            >
              aditya@physicsnotebook.in
            </a>
          </p>
        </div>
      </section>
    </>
  );
};

export default SolutionsPage;
